import { FinalImpactScoreType, NetScoreFormat } from '@upright/common/model/output/scores/types';
import { E2eTestElementId } from '@upright/test-ids/dom-elements';
import { TippyWithOriginalStyles } from '@upright/ui/common/generic/components/tooltips/default-tippy';
import { bouTextFontStyle } from '@upright/ui/common/generic/content/texts/bou-text/text-style';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { scoreTextColor } from '@upright/ui/common/generic/utilities/format';
import { useLensImpactProfileFontSizes } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/font-sizes';
import { Bounds } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { useCarouselContext, useCarouselLayout } from '@upright/ui/common/upright/core/impact-lens-profile/context';
import { useNetImpactLensContext } from '@upright/ui/common/upright/core/impact-lenses/net-impact/common/use-net-impact-lens-context';
import { AggregateMetricProps } from '@upright/ui/common/upright/model/output/scores/aggregate-metrics';
import {
  NirFormulaWithLightText,
  NisFormulaWithLightText,
} from '@upright/ui/common/upright/model/output/scores/aggregate-metrics/styles';
import {
  formatNetScore,
  getImpactScoreFormattersFromScoreReaders,
} from '@upright/ui/common/upright/model/output/scores/net-impact-chart/value-formatter';
import { getValueSetName } from '@upright/ui/untidy/redux/selectors';
import { isNil } from 'lodash';
import { CSSProperties, ReactFragment } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { NetScore } from '../use-net-score';
import { AggregateMetricInfo } from './aggregate-metric-info';
import { Box } from '@primer/react';

export type NetScoreRowProps = {
  netScore: NetScore | undefined;
  valueSetName: string;
  bounds: Bounds;
} & AggregateMetricProps;

export function NetScoreBlock(props: NetScoreRowProps) {
  const fontSizes = useLensImpactProfileFontSizes();
  const bounds = props.bounds;
  const height = fontSizes.netScore * 1.65;
  const style: CSSProperties = {
    left: bounds.x1,
    width: bounds.width,
    height: bounds.height,
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: bounds.y1,
        height: bounds.height,
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'border.default',
      }}
    >
      <VerticalLine position="top" />
      <VerticalLine position="bottom" />
      <Box
        style={style}
        sx={{
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <svg width={bounds.width} height={height} style={{ display: 'block' }}>
          <NetScoreRowMiddlePart {...props} />
        </svg>
        <NetScoreRowRightPart {...props} />
      </Box>
    </Box>
  );
}

const VerticalLine = ({ position }: { position: 'top' | 'bottom' }) => (
  <Box
    sx={{
      position: 'absolute',
      top: position === 'top' ? 0 : 'auto',
      bottom: position === 'bottom' ? 0 : 'auto',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 1,
      height: 16,
      bg: 'border.default',
    }}
  />
);

function NetScoreRowMiddlePart(props: NetScoreRowProps) {
  const context = useNetImpactLensContext();
  const options = context?.options;
  const value = props.netScore?.value;
  const { formatters, negativeColor, positiveColor, scoreUnit = FinalImpactScoreType.RELATIVE } = options ?? {};
  const activeValueSetName = useSelector(getValueSetName);
  const layout = useCarouselLayout();
  const area = layout.aggregateResultArea();
  const { netScore: fontSize } = useLensImpactProfileFontSizes();
  const boxHeight = fontSize * 1.65; // TODO
  const isActive = props.valueSetName === activeValueSetName;
  const netScoreFormat = props.currentMetric;

  // Formatting function depends on the scores, so that is kind of needed to show this component.
  if (!value || !formatters) {
    return <g />;
  }

  // TODO: Add spinner to tell if scores are updating
  const formattedScore = formatNetScore(value, netScoreFormat, formatters, scoreUnit);

  return (
    <g transform={`translate(${area.width / 2}, ${boxHeight / 2})`}>
      <TippyWithOriginalStyles
        trigger={'mouseenter focus'}
        placement={'bottom'}
        content={
          <NetScoreFormulaExplanation
            netScoreFormat={netScoreFormat}
            netScore={props.netScore}
            formattedScore={formattedScore}
          />
        }
      >
        <ScoreText
          id={E2eTestElementId.NET_SCORE}
          data-testid={E2eTestElementId.NET_SCORE}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fontSize,
            fill: isActive ? scoreTextColor(value, negativeColor, positiveColor) : BouColor.TOOL_TEXT_GRAY,
            cursor: 'help',
          }}
        >
          {formattedScore}
        </ScoreText>
      </TippyWithOriginalStyles>
    </g>
  );
}

function NetScoreFormulaExplanation(props: {
  netScoreFormat: NetScoreFormat;
  netScore: NetScore | undefined;
  formattedScore: string | number | boolean | JSX.Element | ReactFragment | null | undefined;
}) {
  const { netScoreFormat, netScore } = props;
  const posSum = netScore?.valenceSums.pos;
  const negSum = netScore?.valenceSums.neg;
  const context = useNetImpactLensContext();
  const scoreReader = context?.options.scoreReader;

  if (!scoreReader || isNil(posSum) || isNil(negSum)) return <></>;

  const formatters = getImpactScoreFormattersFromScoreReaders([scoreReader]);

  const formatter = formatters[context.options.scoreUnit];

  const formattedPosSum = formatter(posSum);
  const formattedNegSum = formatter(negSum);

  return (
    <div style={{ position: 'relative' }}>
      {netScoreFormat === NetScoreFormat.NET_SUM ? (
        <NisFormulaExplanation
          posSum={formattedPosSum}
          negSum={formattedNegSum}
          formattedScore={props.formattedScore}
        />
      ) : (
        <NirFormulaExplanation
          posSum={formattedPosSum}
          negSum={formattedNegSum}
          formattedScore={props.formattedScore}
        />
      )}
    </div>
  );
}

function NirFormulaExplanation(props: {
  posSum: string;
  negSum: string;
  formattedScore: string | number | boolean | JSX.Element | ReactFragment | null | undefined;
}) {
  return (
    <div>
      Ratio of all positive and negative impacts. Calculated as{' '}
      <NirFormulaWithLightText positiveText={'positive impacts'} negativeText={'negative impacts'} />, i.e.{' '}
      <NirFormulaWithLightText positiveText={props.posSum} negativeText={props.negSum} /> = {props.formattedScore}.
      Possible theoretical range for net impact ratio is {`]`}-∞, 100%{`]`}.
    </div>
  );
}

function NisFormulaExplanation(props: {
  posSum: string;
  negSum: string;
  formattedScore: string | number | boolean | JSX.Element | ReactFragment | null | undefined;
}) {
  return (
    <div>
      Net sum of all positive and negative impacts in the selected unit of measure. Calculated as{' '}
      <NisFormulaWithLightText positiveText="positive impacts" negativeText="negative impacts" />, i.e.{' '}
      <NisFormulaWithLightText positiveText={props.posSum} negativeText={props.negSum} /> = {props.formattedScore}.
    </div>
  );
}

function NetScoreRowRightPart(props: NetScoreRowProps) {
  const { desktopMode } = useCarouselContext();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const layout = useCarouselLayout();
  const area = layout.aggregateResultArea();
  const { scoreTypeText: fontSize } = useLensImpactProfileFontSizes();

  const x = desktopMode ? area.width / 2 + 86 : 10;

  return (
    <NetScoreRowRightPartContainer style={{ left: x, fontSize }}>
      <AggregateMetricInfo {...props} />
    </NetScoreRowRightPartContainer>
  );
}

const NetScoreRowRightPartContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  left: 30px;
  top: 50%;
  bottom: 50%;
  position: absolute;
`;

const ScoreText = styled.text`
  ${bouTextFontStyle};
  font-weight: bold;
`;
